import * as React from "react";
import User from '@app/Interface/UserInterface';
import NotificationInterface from "@app/Interface/NotificationInterface";
import { Button, DropdownGroup, DropdownItem, Split, SplitItem } from "@patternfly/react-core";
import { TimesIcon } from "@patternfly/react-icons";
import NotificationGroup from "./NotificationGroup";
import ProducerInterface from "@app/Interface/ProducerInterface";
import PageInterface from "@app/Interface/PageInterface";
import { useHistory } from "react-router";
import { NotificationContext, DismissNotification } from "@app/Context/NotificationContext";
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import { ToastNotificationType } from "@app/Interface/ToastNotificationInterface";
import { PageRouteContext } from '@app/Context/PageRouteContext';
import PageRouteHelper from "@app/Service/PageRouteHelper";
import { Link } from "react-router-dom";
import AppSettings from "@app/utils/AppSettings";
import TemplateDefinitions from "@app/utils/TemplateDefinitions";

interface NotificationItemsProps {
    currentUser: User;
    notifications: NotificationInterface[];
    producers: ProducerInterface[];
    onFpuChange: (fpu: ProducerInterface) => void;
    chartPages: PageInterface[];
}

const NotificationItems: React.FC<NotificationItemsProps> = ({ currentUser, notifications, producers, onFpuChange, chartPages }) => {

    const history = useHistory();

    const [fpuNotifications, setFpuNotifications] = React.useState<NotificationInterface[]>([]);

    const [otherFpuNotifications, setOtherFpuNotifications] = React.useState<NotificationInterface[]>([]);

    const [otherFpuIds, setOtherFpuIds] = React.useState<number[]>([]);

    const { notificationDispatch } = React.useContext(NotificationContext);

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const { pageRouteState } = React.useContext(PageRouteContext);

    const notificationsPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_NOTIFICATIONS_PAGE);

    const handleDismiss = async (event: React.SyntheticEvent, notification: NotificationInterface) => {

        event.preventDefault();

        DismissNotification(notificationDispatch, notification);

        // create a toast notification
        const notificationType: ToastNotificationType = ToastNotificationType.Success;

        const toastTitle = "Success";

        const toastDescription = "Notification was dismissed.";

        CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDismissAll = async (event: React.SyntheticEvent) => {

        if (fpuNotifications.length) {

            // loop through each notification for current fpu and dismiss
            fpuNotifications.forEach(notification => {

                DismissNotification(notificationDispatch, notification);

            });

            // create a toast notification
            const notificationType: ToastNotificationType = ToastNotificationType.Success;

            const toastTitle = "Success";

            const toastDescription = "All notifications for the current FPU have been dismissed.";

            CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

        }

    };

    const handleViewChart = (event: React.SyntheticEvent, notification: NotificationInterface) => {

        if (chartPages.length) {

            const pageRoute = PageRouteHelper.getPageRouteByChartType(pageRouteState.pageRoutes, notification.analyte);

            if (pageRoute.id) {

                history.push(pageRoute.path);

            } else {

                console.group("handleViewChart");
                console.warn("Page route not found");
                console.info("notification: ", notification);
                console.info("pageRoutes: ", pageRouteState.pageRoutes);
                console.groupEnd();

            }

        } else {

            console.group("handleViewChart");
            console.warn("Chart pages not loaded.");
            console.info("notification: ", notification);
            console.groupEnd();

        }

    };

    // get notifications for current fpu
    React.useEffect(() => {

        const fpuNotifications = notifications.filter(notification => notification.fpu === currentUser.currentFpuId && notification.dismissed === false);

        setFpuNotifications(fpuNotifications);

    }, [currentUser, notifications]);

    // get the other fpu ids the user may have access to.
    // this is used to show notifications from other fpus.
    React.useEffect(() => {

        const filteredNotifications = notifications.filter(notification => notification.fpu !== currentUser.currentFpuId && notification.dismissed === false);

        setOtherFpuNotifications(filteredNotifications);

        // see: https://thewebdev.info/2021/02/25/how-to-get-distinct-values-from-an-array-of-objects-in-javascript-2/
        const fpuIds = filteredNotifications.map(notification => notification.fpu).filter((value, index, self) => self.indexOf(value) === index);

        setOtherFpuIds(fpuIds);

    }, [currentUser.currentFpuId, notifications]);

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    return (
        <DropdownGroup style={{maxWidth: '90vw'}} key="notification-group">
            {fpuNotifications.map((notification) =>
                <DropdownItem
                    key={notification.id}
                    component="div"
                    style={{maxWidth: '90vw'}}
                >
                    <Split hasGutter isWrappable>
                        <SplitItem isFilled>
                            <div className="pf-u-text-wrap">{notification.title}</div>
                            <Button variant="link" className="pf-u-p-0" onClick={(event) => handleViewChart(event, notification)}>See chart</Button>
                        </SplitItem>
                        <SplitItem>
                            <Button variant="plain" aria-label="Action" onClick={event => handleDismiss(event, notification)}>
                                <TimesIcon color="black" />
                            </Button>
                        </SplitItem>
                    </Split>
                </DropdownItem>
            )}
            {otherFpuIds.map(fpuId =>
                <NotificationGroup key={`fpu-group-${fpuId}`} fpuId={fpuId} notifications={otherFpuNotifications} producers={producers} onFpuChange={onFpuChange} />
            )}
            <DropdownItem key="tools" component="div">
                <Split hasGutter>
                    <SplitItem isFilled>
                        <Button
                            variant="link"
                            className="pf-u-p-0"
                            component={props => <Link {...props} to={{ pathname: notificationsPageRoute.path }} />}
                        >
                            Edit Notifications
                        </Button>
                    </SplitItem>
                    <SplitItem isFilled className="pf-u-text-align-right">
                        <Button variant="link" className="pf-u-p-0" onClick={handleDismissAll}>Clear Notifications</Button>
                    </SplitItem>
                </Split>
            </DropdownItem>
        </DropdownGroup>
    );

}

export default NotificationItems;
